import { useCallback, useEffect, useState } from 'react'
import type { DependencyList } from 'react'
import { errMessage } from '~/lib/errors'

export interface AsyncResource<T> {
    data: T | null
    loading: boolean
    error: string | null
    reload: () => void
}

/**
 * 조회형 비동기 자원 — {data, loading, error, reload}.
 * deps가 바뀌면 자동 재조회. fetch+로딩+에러 try/catch 복붙을 대체한다.
 */
export function useAsyncResource<T>(
    loader: () => Promise<T>,
    deps: DependencyList,
    fallbackMessage = '불러오지 못했어요.'
): AsyncResource<T> {
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const run = useCallback(() => {
        let alive = true
        setLoading(true)
        setError(null)
        loader()
            .then((result) => {
                if (alive) setData(result)
            })
            .catch((err: unknown) => {
                if (alive) setError(errMessage(err, fallbackMessage))
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)

    useEffect(() => run(), [run])

    return { data, loading, error, reload: () => void run() }
}

export interface AsyncAction<A extends unknown[], R> {
    run: (...args: A) => Promise<R | undefined>
    pending: boolean
    error: string | null
    reset: () => void
}

/**
 * 실행형 비동기 액션(저장/삭제/제출 등) — {run, pending, error}.
 * run은 성공 시 결과를, 실패 시 undefined를 반환하고 error를 채운다.
 */
export function useAsyncAction<A extends unknown[], R>(
    fn: (...args: A) => Promise<R>,
    fallbackMessage = '처리하지 못했어요.'
): AsyncAction<A, R> {
    const [pending, setPending] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const run = useCallback(
        async (...args: A): Promise<R | undefined> => {
            setPending(true)
            setError(null)
            try {
                return await fn(...args)
            } catch (err: unknown) {
                setError(errMessage(err, fallbackMessage))
                return undefined
            } finally {
                setPending(false)
            }
        },
        [fn, fallbackMessage]
    )

    return { run, pending, error, reset: () => setError(null) }
}
